import { z } from "zod";
import { COST_CATEGORIES, COST_TYPE_CODES } from "../catalog/cost-types.ts";

/**
 * Hand-entered store costs (`09-reports.md` § 13).
 *
 * The store costs report is the only one whose inputs a merchant types in, so
 * it needs a write surface as well as a read one. Everything the report adds
 * up comes from here or from the inventory ledger; nothing else in the product
 * knows what rent costs.
 */

const Money = z.string().regex(/^\d+$/, "minor units, as digits");
const BusinessDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "a business date");

export const CostEntrySchema = z.object({
  id: z.uuid(),
  businessDate: BusinessDate,
  category: z.enum(COST_CATEGORIES),
  typeCode: z.enum(COST_TYPE_CODES),
  amount: Money,
  currency: z.string(),
  note: z.string().nullable(),
  enteredByName: z.string().nullable(),
  createdAt: z.string(),
});
export type CostEntry = z.infer<typeof CostEntrySchema>;

/**
 * The category is not in the body: `COST_TYPES` decides it. A caller that
 * could send both could file rent under labour, and the subtotal a merchant
 * reads would be wrong in a way nothing would ever flag.
 */
export const SaveCostEntrySchema = z.object({
  businessDate: BusinessDate,
  typeCode: z.enum(COST_TYPE_CODES),
  amount: Money,
  note: z.string().max(240).nullable().default(null),
});

/**
 * Spelled out rather than derived with `.partial()`.
 *
 * `.partial()` makes a field optional but **keeps its `.default()`**, so an
 * omitted key arrives as its default rather than as absent — and a PATCH that
 * meant "rename this" would quietly reset everything it did not mention.
 */
export const UpdateCostEntrySchema = z.object({
  businessDate: BusinessDate.optional(),
  typeCode: z.enum(COST_TYPE_CODES).optional(),
  amount: Money.optional(),
  note: z.string().max(240).nullable().optional(),
});

export const CostEntryQuerySchema = z.object({
  from: BusinessDate,
  to: BusinessDate,
  category: z.enum(COST_CATEGORIES).optional(),
  page: z.coerce.number().int().positive().default(1),
  size: z.coerce.number().int().positive().max(200).default(50),
});
